// src/components/accordion/AccConsumption.tsx

import { ExpandMoreTwoTone } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  List,
  Typography,
} from '@mui/material'
import { Dispatch, MouseEvent, SetStateAction } from 'react'

import { FolderId, SensorRecord, SensorId } from '@/types/domain'

import { CorrelationConsumptionItem, HStack } from '..'
import {
  colorSchemeConsumption,
  normalizedSensorType as nst,
  sensorType,
} from '../../api'
import { useFolderSensors } from '../../hooks'

type AccConsumptionProps = {
  data: {
    sensors: Array<SensorRecord>
    selected: Array<SensorId>
  }
  id: FolderId
  onChange: Dispatch<SetStateAction<Array<SensorId>>>
}

export const AccConsumption = ({ id, data, onChange }: AccConsumptionProps) => {
  const { sensors, selected } = data

  const fSensors: SensorRecord[] = useFolderSensors(id)

  const borderLeft = `solid 10px ${colorSchemeConsumption[0]}`

  const isSelected = (id: SensorId) => selected.includes(id)

  return ['energy', 'gas', 'water'].map(medium => {
    const FILTER_MEDIUM = f =>
      nst(f.sensor_type, f.type_in_folder) === medium ||
      nst(f.sensor_type, f.type_in_folder) === `${medium}_detail`

    const modCSensors = sensors.filter(
      f =>
        nst(f.sensor_type.replace('_detail', ''), f.type_in_folder) === medium
    )

    const ALL_SELECTED = modCSensors.every(s => selected.includes(s.entity_id))
    const SOME_SELECTED = modCSensors.some(s => selected.includes(s.entity_id))

    const onClick = (event: MouseEvent) => {
      event.stopPropagation()

      onChange(prev => {
        const ids = modCSensors.map(s => s.entity_id)

        return ALL_SELECTED
          ? prev.filter(id => !ids.includes(id))
          : [...prev, ...ids]
      })
    }

    return (
      sensors.find(f => nst(f.sensor_type, f.type_in_folder) === medium) && (
        <Accordion
          key={medium}
          sx={{ '&.Mui-expanded': { m: '0 !important' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreTwoTone />}
            sx={{ borderLeft }}
          >
            <HStack justifyContent={'space-between'} sx={{ width: 1 }}>
              <Typography>{sensorType(medium)}</Typography>

              <Checkbox
                checked={ALL_SELECTED}
                color={'primary'}
                indeterminate={SOME_SELECTED && !ALL_SELECTED}
                onClick={onClick}
              />
            </HStack>
          </AccordionSummary>

          <AccordionDetails sx={{ borderLeft, p: 0 }}>
            <List sx={{ width: 1 }}>
              <Divider />

              {modCSensors.filter(FILTER_MEDIUM).map(s => {
                const sId: SensorId = Number(s.entity_id)

                const onClick = event => {
                  event.stopPropagation()

                  const record: SensorRecord = fSensors.find(
                    s => s.entity_id === sId
                  )
                  if (!record) return

                  onChange(prev =>
                    prev.includes(sId)
                      ? prev.filter(id => id !== sId)
                      : [...prev, sId]
                  )
                }

                return (
                  <CorrelationConsumptionItem
                    id={sId}
                    key={sId}
                    onClick={onClick}
                    selected={isSelected(sId)}
                  />
                )
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      )
    )
  })
}
